import React, { useEffect, useRef, useState } from 'react';
import SubPage from '../components/site/SubPage';
import { BarChart, DualPanelChart, GroupedColumnChart } from '../components/site/Charts';
import {
  ClosingBrand,
  PageSection,
  PageTitleBox,
  SectionHeading,
  SourceLine,
} from '../components/site/SubPageParts';

const IMG = '/images/site/cash-cow';

/*
 * Counts, losses and definitions all come from the 2025 IC3 Annual Report:
 * crime types by count (p.7), by loss (p.8), definitions (Appendix B, p.59-61).
 */
const SCHEMES = [
  {
    name: 'Cryptocurrency',
    complaints: '181,565',
    losses: '$11.36 Billion',
    body: 'Information reported contains some reference to virtual currency. Recorded by IC3 as a descriptor across crime types rather than a crime type of its own.',
  },
  {
    name: 'Tech / Customer Support',
    complaints: '47,794',
    losses: '$2.13 Billion',
    body: 'Subject posing as technical or customer support/service.',
  },
  {
    name: 'Phishing / Smishing / Spoof',
    complaints: '191,561',
    losses: '$215.8 Million',
    body: 'The use of unsolicited email, text messages, and telephone calls purportedly from a legitimate company requesting personal, financial, and/or login credentials.',
  },
  {
    name: 'Confidence / Romance',
    complaints: '23,159',
    losses: '$929.2 Million',
    body: 'An individual believes they are in a relationship (family, friendly, or romantic) and are tricked into sending money, personal and financial information, or items of value to the perpetrator, or to launder money on their behalf. Includes the Grandparent’s Scheme.',
  },
  {
    name: 'Government Impersonation',
    complaints: '32,424',
    losses: '$797.9 Million',
    body: 'A government official is impersonated to collect or extort money.',
  },
  {
    name: 'Lottery / Sweepstakes / Inheritance',
    complaints: '5,623',
    losses: '$194.1 Million',
    body: 'An individual is contacted about winning a lottery or sweepstakes they never entered, or to collect on an inheritance from an unknown relative.',
  },
];

const AGE_RANGES = [
  { label: 'No Age', value: 16.4, display: '16.4%' },
  { label: '60+', value: 24.2, display: '24.2%' },
  { label: '50 - 59', value: 13.1, display: '13.1%' },
  { label: '40 - 49', value: 14.5, display: '14.5%' },
  { label: '30 - 39', value: 14.3, display: '14.3%' },
  { label: '20 - 29', value: 13.2, display: '13.2%' },
  { label: '20/Under', value: 4.3, display: '4.3%' },
];

const CRYPTO_BY_AGE = [
  { label: '60+', value: 4432224488, display: '$4,432,224,488' },
  { label: '50 - 59', value: 2139033232, display: '$2,139,033,232' },
  { label: '40 - 49', value: 1552700119, display: '$1,552,700,119' },
  { label: '30 - 39', value: 861551625, display: '$861,551,625' },
  { label: '20 - 29', value: 288875060, display: '$288,875,060' },
  { label: 'Under 20', value: 26955462, display: '$26,955,462' },
];

const STATES_BY_COMPLAINTS = [
  { label: 'California', value: 116414, display: '116,414' },
  { label: 'Texas', value: 97912, display: '97,912' },
  { label: 'Florida', value: 71843, display: '71,843' },
  { label: 'New York', value: 45255, display: '45,255' },
  { label: 'Illinois', value: 32977, display: '32,977' },
  { label: 'Pennsylvania', value: 31154, display: '31,154' },
  { label: 'Arizona', value: 28868, display: '28,868' },
  { label: 'Ohio', value: 27626, display: '27,626' },
  { label: 'North Carolina', value: 25940, display: '25,940' },
  { label: 'Georgia', value: 25936, display: '25,936' },
];

const STATES_BY_LOSS = [
  { label: 'California', value: 3675, display: '$3,675M' },
  { label: 'Texas', value: 1826, display: '$1,826M' },
  { label: 'Florida', value: 1596, display: '$1,596M' },
  { label: 'New York', value: 1226, display: '$1,226M' },
  { label: 'New Jersey', value: 660, display: '$660M' },
  { label: 'Arizona', value: 631, display: '$631M' },
  { label: 'Pennsylvania', value: 538, display: '$538M' },
  { label: 'Illinois', value: 535, display: '$535M' },
  { label: 'Georgia', value: 535, display: '$535M' },
  { label: 'Virginia', value: 476, display: '$476M' },
];

// 2020 Census, "2020 Census: 1 in 6 People in the United States Were 65 and Over".
const OLDER_AGE_GROUPS = [
  { label: '65 to 74 years', values: { y2010: 21.7, y2020: 33.1 } },
  { label: '75 to 84 years', values: { y2010: 13.1, y2020: 16.3 } },
  { label: '85 to 94 years', values: { y2010: 5.1, y2020: 5.7 } },
  { label: '95 years and over', values: { y2010: 0.4, y2020: 0.6 } },
];

const CENSUS_SERIES = [
  { key: 'y2010', label: '2010', color: '#B58200' },
  { key: 'y2020', label: '2020', color: '#0A6EA8' },
];

// Size (bars) and share of total population (line) by decade — Census Figure 1.
const POPULATION_65_PLUS = [
  { label: '1920', bar: 4.9, line: 4.7 },
  { label: '1930', bar: 6.6, line: 5.4 },
  { label: '1940', bar: 9.0, line: 6.8 },
  { label: '1950', bar: 12.3, line: 8.2 },
  { label: '1960', bar: 16.7, line: 9.2 },
  { label: '1970', bar: 20.1, line: 9.8 },
  { label: '1980', bar: 25.5, line: 11.3 },
  { label: '1990', bar: 31.2, line: 12.5 },
  { label: '2000', bar: 35.0, line: 12.4 },
  { label: '2010', bar: 40.3, line: 13.0 },
  { label: '2020', bar: 55.8, line: 16.8 },
];

/**
 * Scroll-snapped rail of the top revenue-producing schemes. Native smooth
 * scrolling keeps the arrows and touch swipe consistent at any breakpoint.
 */
const SchemeCarousel = () => {
  const railRef = useRef(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  const syncEdges = () => {
    const rail = railRef.current;
    if (!rail) return;
    setAtStart(rail.scrollLeft <= 2);
    setAtEnd(rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 2);
  };

  useEffect(() => {
    syncEdges();
    window.addEventListener('resize', syncEdges);
    return () => window.removeEventListener('resize', syncEdges);
  }, []);

  const page = (direction) => {
    const rail = railRef.current;
    if (!rail) return;
    const card = rail.firstElementChild;
    const step = card ? card.offsetWidth + 24 : rail.clientWidth;
    rail.scrollBy({ left: direction * step, behavior: 'smooth' });
  };

  return (
    <div>
      <div className="flex items-center gap-5 text-[22px] md:text-[26px]">
        <button
          type="button"
          onClick={() => page(-1)}
          disabled={atStart}
          aria-label="Previous schemes"
          className="text-black transition-opacity disabled:opacity-25"
        >
          &lt;
        </button>
        <button
          type="button"
          onClick={() => page(1)}
          disabled={atEnd}
          aria-label="Next schemes"
          className="text-black transition-opacity disabled:opacity-25"
        >
          &gt;
        </button>
      </div>

      <div
        ref={railRef}
        onScroll={syncEdges}
        className="mt-4 flex snap-x snap-mandatory gap-6 overflow-x-auto pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {SCHEMES.map((scheme) => (
          <article
            key={scheme.name}
            className="w-[85%] flex-shrink-0 snap-start bg-[#F0F0F0] px-5 py-5 shadow-[4px_4px_0_rgba(0,0,0,0.08)] sm:w-[46%] lg:w-[31.5%]"
          >
            <h3 className="min-h-[3rem] text-[17px] text-black md:text-[19px]">{scheme.name}</h3>
            <div className="my-3 h-px w-full bg-black/30" />
            <div className="flex items-baseline justify-between gap-4">
              <span className="text-[16px] text-black md:text-[18px]">{scheme.complaints}</span>
              <span className="text-[16px] text-black md:text-[18px]">{scheme.losses}</span>
            </div>
            <div className="my-3 h-px w-full bg-black/30" />
            <p className="text-[14px] leading-snug text-black md:text-[15px]">{scheme.body}</p>
          </article>
        ))}
      </div>
    </div>
  );
};

const CashCow = () => (
  <SubPage title="Cash Cow">
    <PageTitleBox>It&rsquo;s not personal &ndash; it&rsquo;s about the money.</PageTitleBox>

    {/* ---------------- Money ---------------- */}
    <PageSection className="pt-4">
      <img
        src={`${IMG}/money.jpeg`}
        alt="A scattered pile of one hundred dollar bills"
        className="h-[260px] w-full object-cover small:aspect-[14/5] small:h-auto"
      />
    </PageSection>

    {/* ---------------- Fraud and schemes ---------------- */}
    <PageSection className="pt-10">
      <SectionHeading className="mb-4">Fraud and Schemes.</SectionHeading>
      <SchemeCarousel />
      <div className="mt-4">
        <SourceLine>
          Source: FBI 2025 Internet Crime Report &mdash; crime types by count and by loss.
        </SourceLine>
      </div>
    </PageSection>

    {/* ---------------- Expanding Pie ---------------- */}
    <PageSection className="pt-14">
      <SectionHeading>Expanding Pie.</SectionHeading>
      <div className="mt-5 space-y-4 text-[16px] leading-relaxed text-black md:text-[20px]">
        <p>
          The U.S. population age 65 and over grew nearly five times faster than the total
          population over the 100 years from 1920 to 2020, according to the 2020 Census.
        </p>
        <p>
          The older population reached 55.8 million, or{' '}
          <strong className="font-bold">16.8% of the population of the United States in 2020.</strong>
        </p>
      </div>

      <div className="relative mt-8 bg-black">
        <img
          src={`${IMG}/hand-on-laptop.jpeg`}
          alt="A hand poised over a glowing laptop keyboard in the dark"
          className="h-[240px] w-full object-cover small:aspect-[336/115] small:h-auto"
        />
        <img
          src={`${IMG}/seniors-together.jpeg`}
          alt="Four older adults laughing together over a tablet"
          className="absolute right-[6%] top-1/2 w-[26%] -translate-y-1/2 object-cover shadow-xl"
        />
      </div>

      {/* Copy and both figures share one row so the section doesn't wrap. */}
      <div className="mt-8 grid grid-cols-1 items-start gap-6 md:grid-cols-2 lg:grid-cols-[minmax(0,21%)_minmax(0,1fr)_minmax(0,1fr)]">
        <div>
          <p className="text-[15px] text-black md:text-[17px]">2020, the 65&ndash;74 age group:</p>
          <ul className="mt-3 space-y-2.5">
            {[
              'Was the largest of the older age groups with 33.1 million people, representing over half of the 65-and-over population.',
              'Represented 1 in 10 Americans in 2020.',
              'Experienced the largest growth of any older age group the previous decade. Its numbers grew by 11.4 million or 52.5%, increasing from 21.7 million in 2010 to 33.1 million in 2020.',
            ].map((item) => (
              <li
                key={item}
                className="flex gap-2.5 text-[14px] leading-snug text-black md:text-[15px]"
              >
                <span aria-hidden="true" className="mt-[7px] h-1.5 w-1.5 flex-shrink-0 rounded-full bg-black" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        <GroupedColumnChart
          title="Population Size of Older Age Groups: 2010 and 2020"
          groups={OLDER_AGE_GROUPS}
          series={CENSUS_SERIES}
          note="In millions"
          unitSuffix="M"
        />

        <DualPanelChart
          title="Population 65 and Over by Size and Percentage: 1920 to 2020"
          points={POPULATION_65_PLUS}
          barLabel="Millions"
          lineLabel="% of total population"
          barFormat={(v) => `${v}`}
          lineFormat={(v) => `${v}%`}
          labelEvery={2}
        />
      </div>

      <div className="mt-5">
        <SourceLine>
          Source: U.S. Census Bureau &mdash; 2020 Census: United States Older Population Grew
        </SourceLine>
      </div>
    </PageSection>

    {/* ---------------- Hot zone ---------------- */}
    <PageSection className="pt-12">
      <SectionHeading>Hot Zones.</SectionHeading>
      <div className="mt-5 bg-[#F2F2F2] px-4 py-8 md:px-10">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:gap-8">
          <BarChart
            title="Age Ranges of Cyber-Enabled Fraud Reporting"
            bars={AGE_RANGES}
            valueHeading="Share of reports"
            textured
          />
          <BarChart
            title="Crypto Losses by Age Range"
            bars={CRYPTO_BY_AGE}
            valueHeading="Losses"
            labelWidth={80}
          />
          <BarChart
            title="Top 10 States by Number of Complaints"
            bars={STATES_BY_COMPLAINTS}
            valueHeading="Complaints"
            labelWidth={116}
          />
          <BarChart
            title="Top 10 States by Loss"
            bars={STATES_BY_LOSS}
            valueHeading="Losses (millions)"
            labelWidth={116}
          />
        </div>
        <div className="mt-6">
          <SourceLine>
            Source: FBI 2025 Internet Crime Report &mdash; cyber-enabled fraud (p.9),
            cryptocurrency (p.52), complaints by state (p.27).
          </SourceLine>
        </div>
      </div>
    </PageSection>

    {/* ---------------- Closing ---------------- */}
    <PageSection className="pt-12">
      {/* Shown whole at its own 3:1 aspect. Cropped to a fixed height, the
          phone view took the middle of the frame and cut off every head. */}
      <img
        src={`${IMG}/dont-be-a-number.jpeg`}
        alt="Older adults relaxing together at home"
        className="h-auto w-full"
      />
    </PageSection>

    <ClosingBrand tagline="Don’t be a number — be ScammerAware." />
  </SubPage>
);

export default CashCow;
